import cx from "classnames";
import { FC, lazy, MouseEvent, Suspense, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import { getActiveRoute } from "./../../helpers/getActiveRoute";
import useLayout from "./../../hooks/useLayout";
import { IScreenByType } from "./../../hooks/useMedia";
import useShallowSelector from "./../../hooks/useShalowSelector";
import { changeSelectedItem } from "./../../store/selected/actionCreators";
import { selectIsVerticalView, selectPaginationActiveStatus } from "./../../store/ui/selectors";
import { RouteType } from "./../routes/types";

import { ISelectedItems } from "./../../store/selected/types";
import { changeCurrentField, changeMapScriptStatus } from "./../../store/ui/actionCreators";

import { LoadScript } from "@react-google-maps/api";
import { getApiKey, getLibraries } from "../../helpers/mapProps";
import useFields from "../../hooks/useFields";
import { FieldTypes } from "../../store/fields/types";

import Geocode from "react-geocode";
import { formInitials } from "../../helpers/dataManipulations/initials";
import { selectPageStyles } from "../../store/forms/selectors";
import { selectPages } from "../../store/pages/selectors";


import styles from "./styles.module.scss";

const EditPages = lazy(() => import("./Mods/Edit"));
const DesignPages = lazy(() => import("./Mods/Design"));
const LogicPages = lazy(() => import("./Mods/Logic"));
const ValidationPages = lazy(() => import("./Mods/Validation"));
const IntegrationPages = lazy(() => import("./Mods/Integration"));
const PaymentPages = lazy(() => import("./Mods/Payment"));
const PreviewPages = lazy(() => import("./Mods/Preview"));

interface IProps {
    screenType: IScreenByType;
}

const Content: FC<IProps> = ({ screenType }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { onChange: onLayoutChange, layout, edited, updateLayoutWidths, setHeight: setFieldHeight } = useLayout();
    const activeRoute = getActiveRoute(location.pathname);
    const pages = useShallowSelector(selectPages);
    const pageView = useShallowSelector(selectIsVerticalView);
    const pageStyles = useShallowSelector(selectPageStyles) || formInitials.styles.page;
    const isPaginationActive = useShallowSelector(selectPaginationActiveStatus) && pages.length > 1;
    const scrolableElement = useRef<HTMLDivElement>(null);
    const [loadScript, setLoadScript] = useState(false);
    const mapApiKey = getApiKey();
    const mapLibraries = getLibraries();
    const isMapField = useFields()
        .fields.map(({ type }) => type)
        .includes(FieldTypes.MAP);

    useEffect(() => {
        if (isMapField && !loadScript) setLoadScript(true);
        // eslint-disable-next-line
    }, [isMapField]);

    // eslint-disable-next-line
    useEffect(() => updateLayoutWidths, [screenType]);

    let modeContent;
    switch (activeRoute) {
        case RouteType.Edit:
            modeContent = (
                <EditPages
                    pageView={pageView}
                    scrolableElement={scrolableElement}
                    screenType={screenType}
                    onLayoutChange={onLayoutChange}
                    setFieldHeight={setFieldHeight}
                    layout={{ list: layout, edited }}
                    pageStyles={pageStyles}
                />
            );
            break;
        case RouteType.Design:
            modeContent = (
                <DesignPages
                    pageView={pageView}
                    scrolableElement={scrolableElement}
                    screenType={screenType}
                    onLayoutChange={onLayoutChange}
                    setFieldHeight={setFieldHeight}
                    pageStyles={pageStyles}
                    layout={{ list: layout, edited }}
                />
            );
            break;
        case RouteType.Logic:
            modeContent = (
                <LogicPages
                    pageView={pageView}
                    scrolableElement={scrolableElement}
                    screenType={screenType}
                    onLayoutChange={onLayoutChange}
                    pageStyles={pageStyles}
                    setFieldHeight={setFieldHeight}
                    layout={{ list: layout, edited }}
                />
            );
            break;
        case RouteType.Validation:
            modeContent = (
                <ValidationPages
                    pageView={pageView}
                    scrolableElement={scrolableElement}
                    screenType={screenType}
                    onLayoutChange={onLayoutChange}
                    pageStyles={pageStyles}
                    setFieldHeight={setFieldHeight}
                    layout={{ list: layout, edited }}
                />
            );
            break;
        case RouteType.Integration:
            modeContent = (
                <IntegrationPages
                    pageView={pageView}
                    scrolableElement={scrolableElement}
                    screenType={screenType}
                    onLayoutChange={onLayoutChange}
                    pageStyles={pageStyles}
                    setFieldHeight={setFieldHeight}
                    layout={{ list: layout, edited }}
                />
            );
            break;
        case RouteType.Payment:
            modeContent = (
                <PaymentPages
                    pageView={pageView}
                    scrolableElement={scrolableElement}
                    screenType={screenType}
                    onLayoutChange={onLayoutChange}
                    pageStyles={pageStyles}
                    setFieldHeight={setFieldHeight}
                    layout={{ list: layout, edited }}
                />
            );
            break;
        case RouteType.Preview:
            modeContent = (
                <PreviewPages
                    pageView={pageView}
                    scrolableElement={scrolableElement}
                    screenType={screenType}
                    onLayoutChange={onLayoutChange}
                    pageStyles={pageStyles}
                    setFieldHeight={setFieldHeight}
                    layout={{ list: layout, edited }}
                />
            );
            break;
        default:
            modeContent = <>Error 404</>;
            break;
    }

    const onClick = (e: MouseEvent<HTMLDivElement>) => {
        dispatch(changeSelectedItem({ item: ISelectedItems.GLOBALS, items: [] }));
        if (e.target === scrolableElement.current) dispatch(changeCurrentField(null));
    };

    return (
        <>
            {loadScript ? (
                <LoadScript
                    googleMapsApiKey={mapApiKey}
                    libraries={mapLibraries}
                    onLoad={() => {
                        dispatch(changeMapScriptStatus({ isLoaded: true }));
                        Geocode.setApiKey(mapApiKey);
                    }}
                />
            ) : null}
            <div
                id="contentWrapper"
                ref={scrolableElement}
                className={cx(styles.wrapper, {
                    [styles.pageView]: pageView,
                    [styles.isPaginationActive]: !pageView && isPaginationActive,
                })}
                onClick={onClick}
            >
                <Suspense fallback={<>Loading...</>}>{modeContent}</Suspense>
            </div>
        </>
    );
};

export default Content;
